import { useCallback } from "react";
import type { ChallengeDetail, ChallengeType } from "../../challenges/types/challengeAdmin";
import type {
  DraftPair,
  DraftPlayer,
  PairMode,
} from "../../challenges/types/challengeDraft";
import {
  createPairId,
  draftSideToChoice,
  normalizeCpf,
} from "../../challenges/utils/challengePairs";
import {
  createManualEventRegistration,
  listEventRegistrations,
  type AdminEventRegistration,
} from "../services/eventRegistrationAdminService";
import {
  isFixedPairOnlyChallengeType,
  normalizeParticipantCpfs,
} from "../utils/adminChallengeEditor";

type UseAdminChallengeRegistrationProvisioningInput = {
  activeRegistrationCategoryKeyForModal: string;
  challengeEventRegistrations: AdminEventRegistration[];
  currentPairMode: PairMode;
  draftPairs: DraftPair[];
  draftPlayers: DraftPlayer[];
  loadChallengeEventRegistrations: (eventId?: string | null) => Promise<void>;
  loadEventRegistrations: () => Promise<void>;
  token?: string | null;
};

export function useAdminChallengeRegistrationProvisioning({
  activeRegistrationCategoryKeyForModal,
  challengeEventRegistrations,
  currentPairMode,
  draftPairs,
  draftPlayers,
  loadChallengeEventRegistrations,
  loadEventRegistrations,
  token,
}: UseAdminChallengeRegistrationProvisioningInput) {
  const resolveParticipantCpfsForPersist = useCallback(
    (detail: ChallengeDetail) => {
      const currentCpfs = normalizeParticipantCpfs(detail.participantCpfs);
      const challengeType = String(detail.challengeType || "")
        .trim()
        .toLowerCase();
      if (challengeType === "tournament") return currentCpfs;
      const isFixedPairChallenge =
        challengeType === "poco" ||
        (challengeType !== "super8" && currentPairMode === "fixed");
      if (!isFixedPairChallenge) return currentCpfs;

      const fromDraftPairs = Array.from(
        new Set(
          draftPairs
            .flatMap((pair) => [
              normalizeCpf(pair.cpf1),
              normalizeCpf(pair.cpf2),
            ])
            .filter((cpf) => cpf.length >= 8),
        ),
      );
      if (fromDraftPairs.length > 0) return fromDraftPairs;

      const fromFixedPairRegistrations = Array.from(
        new Set(
          challengeEventRegistrations
            .filter(
              (row) =>
                row.registrationType === "fixed_pair" &&
                row.status !== "canceled",
            )
            .flatMap((row) => [
              normalizeCpf(row.userCpf),
              normalizeCpf(row.partnerCpf),
            ])
            .filter((cpf) => cpf.length >= 8),
        ),
      );
      if (fromFixedPairRegistrations.length > 0) {
        return fromFixedPairRegistrations;
      }

      return currentCpfs;
    },
    [challengeEventRegistrations, currentPairMode, draftPairs],
  );

  const autoCreateMissingRegistrationsAndCharges = useCallback(
    async ({
      eventId,
      challengeType,
    }: {
      eventId: string;
      challengeType: ChallengeType;
    }) => {
      if (!token) return;
      const safeEventId = String(eventId || "").trim();
      if (!safeEventId) return;

      const existing = await listEventRegistrations(token, {
        status: "all",
        eventId: safeEventId,
      });

      const isActiveRow = (row: AdminEventRegistration) =>
        String(row.status || "").trim().toLowerCase() !== "canceled";
      const isTournamentSave =
        String(challengeType || "").trim().toLowerCase() === "tournament";
      const registrationScopeKey = isTournamentSave
        ? String(activeRegistrationCategoryKeyForModal || "").trim() ||
          "sem-categoria"
        : "evento";
      const scopedRegistrationKey = (
        type: "pair" | "player",
        value: string,
        categoryKey = registrationScopeKey,
      ) => `${type}:${categoryKey}:${value}`;

      const existingPairIds = new Set(
        existing
          .filter(
            (row) =>
              row.registrationType === "fixed_pair" &&
              isActiveRow(row) &&
              normalizeCpf(row.userCpf).length >= 8 &&
              normalizeCpf(row.partnerCpf).length >= 8,
          )
          .map((row) => {
            const pairId = createPairId(
              normalizeCpf(row.userCpf),
              normalizeCpf(row.partnerCpf),
            );
            const categoryKey = isTournamentSave
              ? String(row.registrationCategoryKey || "").trim() ||
                "sem-categoria"
              : "evento";
            return scopedRegistrationKey("pair", pairId, categoryKey);
          }),
      );

      const existingCpfs = new Set(
        existing
          .filter(
            (row) =>
              row.registrationType !== "fixed_pair" &&
              isActiveRow(row) &&
              normalizeCpf(row.userCpf).length >= 8,
          )
          .map((row) => {
            const cpf = normalizeCpf(row.userCpf);
            const categoryKey = isTournamentSave
              ? String(row.registrationCategoryKey || "").trim() ||
                "sem-categoria"
              : "evento";
            return scopedRegistrationKey("player", cpf, categoryKey);
          }),
      );

      const shouldUseFixedPairs =
        isFixedPairOnlyChallengeType(challengeType) || currentPairMode === "fixed";
      const shouldPersistPreferredSide =
        String(challengeType || "").trim().toLowerCase() === "challenge" &&
        !shouldUseFixedPairs;
      const createdRegistrationIds: number[] = [];

      if (shouldUseFixedPairs) {
        const uniquePairs = Array.from(
          new Map(
            draftPairs
              .map((pair) => ({
                id: createPairId(
                  normalizeCpf(pair.cpf1),
                  normalizeCpf(pair.cpf2),
                ),
                cpf1: normalizeCpf(pair.cpf1),
                cpf2: normalizeCpf(pair.cpf2),
              }))
              .filter(
                (pair) =>
                  pair.cpf1.length >= 8 &&
                  pair.cpf2.length >= 8 &&
                  pair.cpf1 !== pair.cpf2,
              )
              .map((pair) => [pair.id, pair]),
          ).values(),
        );

        for (const pair of uniquePairs) {
          const pairKey = scopedRegistrationKey("pair", pair.id);
          if (existingPairIds.has(pairKey)) continue;
          const created = await createManualEventRegistration(
            token,
            safeEventId,
            {
              cpf: pair.cpf1,
              participantMode: "dupla_fixa",
              teammateCpf: pair.cpf2,
              registrationCategoryKey: activeRegistrationCategoryKeyForModal,
              confirmPayment: false,
              adminNote: "manual-admin-auto",
              allowEligibilityOverride: true,
              overrideReason: "admin_pair_category_override",
            },
          );
          if (Number.isFinite(Number(created?.id))) {
            createdRegistrationIds.push(Number(created.id));
            existingPairIds.add(pairKey);
          }
        }
      } else {
        const uniqueCpfs = Array.from(
          new Map(
            draftPlayers
              .map((player) => ({
                cpf: normalizeCpf(player.cpf),
                preferredSide: draftSideToChoice(player.side) || "ambos",
              }))
              .filter((player) => player.cpf.length >= 8)
              .map((player) => [player.cpf, player]),
          ).values(),
        );

        for (const player of uniqueCpfs) {
          const cpfKey = scopedRegistrationKey("player", player.cpf);
          if (existingCpfs.has(cpfKey)) continue;
          const created = await createManualEventRegistration(
            token,
            safeEventId,
            {
              cpf: player.cpf,
              participantMode: "individual",
              ...(shouldPersistPreferredSide
                ? { preferredSide: player.preferredSide }
                : {}),
              registrationCategoryKey: activeRegistrationCategoryKeyForModal,
              confirmPayment: false,
              adminNote: "manual-admin-auto",
            },
          );
          if (Number.isFinite(Number(created?.id))) {
            createdRegistrationIds.push(Number(created.id));
            existingCpfs.add(cpfKey);
          }
        }
      }

      if (createdRegistrationIds.length > 0) {
        await loadEventRegistrations();
        await loadChallengeEventRegistrations(safeEventId);
      }
    },
    [
      activeRegistrationCategoryKeyForModal,
      currentPairMode,
      draftPairs,
      draftPlayers,
      loadChallengeEventRegistrations,
      loadEventRegistrations,
      token,
    ],
  );

  return {
    autoCreateMissingRegistrationsAndCharges,
    resolveParticipantCpfsForPersist,
  };
}
